import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Progresso, STATUS_CAMPANHA } from '../components/ui';
import { ehHtmlEmail, htmlParaTexto, linkWaMe } from '@crm/contracts';
import { dataHora, formatarWhatsapp, moeda, num } from '../format';

interface Prog {
  total: number; enviados: number; pendentes: number; pulados: number; bloqueados: number;
  pendentesWhatsapp: number; email: number; emailEnviados: number; emailPendentes: number; falhas: number;
}
interface Detalhe {
  id: string; nome: string; mensagem: string; status: string;
  canal: 'WHATSAPP' | 'EMAIL' | 'PREFERENCIA'; canalAlternativo: boolean; emailAssunto: string | null; emailCorpo: string | null; atribuicao: string; criadoPorNome: string; totalClientes: number;
  iniciadaEm: string | null; progresso: Prog; meuProgresso: Prog; podeGerenciar: boolean;
  porResponsavel: Array<{ id: string; nome: string; total: number; enviados: number; pendentes: number }>;
  oportunidades: { criadas: number; ganhas: number; receita: string };
  /** Botão "Falar no WhatsApp" do e-mail (specs/24) */
  whatsappBotao?: boolean; whatsappStatusId?: string | null;
  whatsapp?: { comBotao: number; cliques: number } | null;
}
interface Clique {
  clienteId: string; nome: string; empresa: string | null; whatsapp: string | null; vendedor: string; clicouEm: string;
  statusAtual: string; statusGrupo: string; statusAnterior: string | null;
}

/** Quem clicou no botão WhatsApp do e-mail (specs/24): o vendedor confere a conversa. */
function ClicaramWhatsapp({ campanhaId, statusId }: { campanhaId: string; statusId: string | null }) {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['campanha-cliques', campanhaId], queryFn: () => api<{ data: Clique[] }>(`/campanhas/${campanhaId}/cliques`) });
  const lista = q.data?.data ?? [];
  return (
    <section className="card flush" aria-labelledby="h-cliques">
      <div className="card-head" style={{ padding: '14px 16px', flexWrap: 'wrap' }}>
        <div className="col" style={{ gap: 2 }}>
          <h2 id="h-cliques">Clicaram no WhatsApp</h2>
          <span className="small faint">{statusId ? 'Foram para o status escolhido na campanha (menos quem já era cliente ativo, se marcado).' : 'O clique fica na ficha; o status não muda nesta campanha.'}</span>
        </div>
        {statusId && lista.length > 0 && (
          <button className="btn sm" onClick={() => nav(`/campanhas/nova?statusId=${statusId}&canal=EMAIL`)}>Nova campanha para quem clicou</button>
        )}
      </div>
      {q.isLoading ? <Carregando /> : !lista.length ? <div className="empty">Ninguém clicou ainda.</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Cliente</th><th>Vendedor</th><th>Quando</th><th>Status</th><th /></tr></thead>
            <tbody>
              {lista.map((l) => (
                <tr key={l.clienteId}>
                  <td><Link to={`/clientes/${l.clienteId}`} style={{ color: 'var(--text)' }}>{l.empresa ?? l.nome}</Link>{l.empresa && <span className="small faint"> · {l.nome}</span>}</td>
                  <td>{l.vendedor}</td>
                  <td className="num">{dataHora(l.clicouEm)}</td>
                  <td>{l.statusAtual}{l.statusAnterior && <span className="small faint" style={{ display: 'block' }}>era {l.statusAnterior}</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {l.whatsapp && <a className="btn sm" href={linkWaMe(l.whatsapp, '')} target="_blank" rel="noreferrer">Abrir conversa</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
interface Preview {
  total: number; naoContactar: number; semCanal: number; semVendedor: number; elegiveis: number;
  porWhatsapp: number; porEmail: number; viaAlternativo: number;
  porResponsavel: Array<{ id: string; nome: string; qtd: number; qtdEmail: number; caixaOk: boolean }>;
  amostra: Array<{ clienteId: string; nome: string; texto: string }>;
  amostraEmail: Array<{ clienteId: string; nome: string; assunto: string; texto: string }>;
  avisos: string[]; saldoContatosMes: number | null; emailLimiteHora: number | null; emailLimiteDia: number | null;
}
interface Itens {
  data: Array<{
    id: string; status: string; canal: 'WHATSAPP' | 'EMAIL'; motivo: string | null; enviadoEm: string | null; clienteId: string;
    nome: string; empresa: string | null; whatsapp: string | null; email: string | null; responsavel: string;
  }>;
  total: number;
}
const ROTULO_CANAL = { WHATSAPP: 'WhatsApp', EMAIL: 'E-mail', PREFERENCIA: 'Preferência do cliente' } as const;

const STATUS_ITEM: Record<string, { label: string; cls: string }> = {
  PENDENTE: { label: 'Pendente', cls: '' }, ABERTO: { label: 'Aberto', cls: 'amber' }, ENVIADO: { label: 'Enviado', cls: 'green' },
  PULADO: { label: 'Pulado', cls: '' }, BLOQUEADO: { label: 'Bloqueado', cls: 'red' }, FALHOU: { label: 'Falhou', cls: 'red' },
};

export function CampanhaDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { pode } = useAuth();
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('');
  const [acao, setAcao] = useState(false);

  const q = useQuery({ queryKey: ['campanha', id], queryFn: () => api<Detalhe>(`/campanhas/${id}`) });
  const rascunho = q.data?.status === 'RASCUNHO';
  const prev = useQuery({ queryKey: ['preview', id], queryFn: () => api<Preview>(`/campanhas/${id}/preview`, { method: 'POST' }), enabled: rascunho });
  const itens = useQuery({
    queryKey: ['itens', id, filtro],
    queryFn: () => api<Itens>(`/campanhas/${id}/itens${filtro ? `?status=${filtro}` : ''}`),
    enabled: !!q.data && !rascunho,
  });

  async function executar(path: string, confirmar?: string) {
    if (confirmar && !window.confirm(confirmar)) return;
    setErro(null);
    setAcao(true);
    try {
      await api(`/campanhas/${id}/${path}`, { method: 'POST' });
      await Promise.all(['campanha', 'campanhas', 'itens', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setAcao(false);
    }
  }

  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const c = q.data!;
  const st = STATUS_CAMPANHA[c.status]!;
  const p = c.progresso;

  return (
    <>
      <Link to="/campanhas" className="small">← Campanhas</Link>
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}><h1>{c.nome}</h1><span className={`badge ${st.cls}`}>{st.label}</span></div>
          <span className="muted small">{ROTULO_CANAL[c.canal]} · criada por {c.criadoPorNome}{c.iniciadaEm ? ` · iniciada ${dataHora(c.iniciadaEm)}` : ''}</span>
        </div>
        <div className="actions">
          {rascunho && c.podeGerenciar && <Link className="btn" to={`/campanhas/${c.id}/editar`}>Editar</Link>}
          {c.status === 'EM_EXECUCAO' && c.podeGerenciar && <button className="btn" disabled={acao} onClick={() => executar('pausar')}>Pausar</button>}
          {c.status === 'PAUSADA' && c.podeGerenciar && <button className="btn" disabled={acao} onClick={() => executar('retomar')}>Retomar</button>}
          {['RASCUNHO', 'EM_EXECUCAO', 'PAUSADA'].includes(c.status) && c.podeGerenciar && (
            <button className="btn danger" disabled={acao} onClick={() => executar('cancelar', 'Cancelar a campanha? Os itens pendentes não serão mais trabalhados.')}>Cancelar</button>
          )}
          {c.status === 'EM_EXECUCAO' && c.meuProgresso.pendentesWhatsapp > 0 && pode('CAMPANHAS_EXECUTAR') && (
            <>
            <Link className="btn primary" to={`/campanhas/${c.id}/fila`}><Icon name="whatsapp" size={15} />Abrir minha fila ({c.meuProgresso.pendentesWhatsapp})</Link>
            <Link className="btn" to={`/campanhas/${c.id}/lote`} title="Abre os clientes no app WhatsApp Desktop, um atrás do outro, e você confirma todos no fim">Modo lote (WhatsApp Desktop)</Link>
            </>
          )}
        </div>
      </div>
      <Erro erro={erro} />

      {rascunho ? (
        <div className="row" style={{ gap: 20 }}>
          <section className="card" style={{ flex: '1.2 1 420px' }}>
            {c.canal !== 'EMAIL' && <>
              <h2>{c.canal === 'PREFERENCIA' ? 'Mensagem do WhatsApp' : 'Mensagem'}</h2>
              <div className="bubble" style={{ whiteSpace: 'pre-wrap' }}>{c.mensagem}</div>
            </>}
            {c.canal !== 'WHATSAPP' && <>
              <h2 style={{ marginTop: 8 }}>E-mail</h2>
              <div className="bubble" style={{ whiteSpace: 'pre-wrap' }}><strong style={{ fontWeight: 600 }}>{c.emailAssunto}</strong>{'\n\n'}{c.emailCorpo && ehHtmlEmail(c.emailCorpo) ? htmlParaTexto(c.emailCorpo) : c.emailCorpo}</div>
            </>}
            <h2 style={{ marginTop: 8 }}>Como os clientes vão ler</h2>
            {prev.isLoading && <Carregando />}
            {prev.data?.amostra.map((a) => (
              <div key={a.clienteId} className="field"><span>{a.nome} · WhatsApp</span>
                <div className="bubble" style={{ alignSelf: 'flex-end', background: '#173A2E', color: '#E7F6EF', border: 0 }}>{a.texto}</div></div>
            ))}
            {prev.data?.amostraEmail.map((a) => (
              <div key={a.clienteId} className="field"><span>{a.nome} · e-mail</span>
                <div className="previa-email"><div className="assunto">{a.assunto}</div>{a.texto}</div></div>
            ))}
            {prev.data?.avisos.map((a) => <div key={a} className="alert warn">{a}</div>)}
          </section>
          <aside className="col" style={{ flex: '1 1 340px' }}>
            {prev.error && <Erro erro={mensagemErro(prev.error)} />}
            {prev.data && (
              <section className="card">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}><span className="num" style={{ fontSize: 40, fontWeight: 600, lineHeight: 1.1 }}>{num(prev.data.elegiveis)}</span><span className="muted">clientes entram na campanha</span></div>
                {c.canal !== 'WHATSAPP' && (
                  <div className="col" style={{ gap: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 48px', gap: 10, alignItems: 'center', fontSize: 13 }}>
                      <span>WhatsApp</span><Progresso valor={prev.data.porWhatsapp} total={prev.data.elegiveis} /><span className="num" style={{ textAlign: 'right' }}>{num(prev.data.porWhatsapp)}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 48px', gap: 10, alignItems: 'center', fontSize: 13 }}>
                      <span>E-mail</span><Progresso valor={prev.data.porEmail} total={prev.data.elegiveis} /><span className="num" style={{ textAlign: 'right' }}>{num(prev.data.porEmail)}</span>
                    </div>
                    {prev.data.viaAlternativo > 0 && <span className="small faint">{num(prev.data.viaAlternativo)} pelo canal alternativo (não têm o canal escolhido)</span>}
                    {prev.data.porEmail > 0 && prev.data.emailLimiteHora && <span className="small faint">E-mails saem sozinhos: até {prev.data.emailLimiteHora} por hora e {prev.data.emailLimiteDia} por dia por vendedor.</span>}
                  </div>
                )}
                <div className="small" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex' }}><span className="muted">Atendem aos filtros</span><span className="num" style={{ marginLeft: 'auto' }}>{num(prev.data.total)}</span></div>
                  <div style={{ display: 'flex' }}><span className="muted">− Não contactar</span><span className="num" style={{ marginLeft: 'auto', color: 'var(--red-text)' }}>{num(prev.data.naoContactar)}</span></div>
                  {prev.data.semCanal > 0 && <div style={{ display: 'flex' }}><span className="muted">− {c.canal === 'WHATSAPP' ? 'Sem WhatsApp' : c.canal === 'EMAIL' ? 'Sem e-mail válido' : 'Sem o canal'} (completar cadastro)</span><span className="num" style={{ marginLeft: 'auto', color: 'var(--amber-text)' }}>{num(prev.data.semCanal)}</span></div>}
                  <div style={{ display: 'flex' }}><span className="muted">− Sem vendedor ativo</span><span className="num" style={{ marginLeft: 'auto', color: 'var(--amber-text)' }}>{num(prev.data.semVendedor)}</span></div>
                </div>
                {prev.data.porResponsavel.length > 0 && <>
                  <span className="small faint">Fila por vendedor</span>
                  {prev.data.porResponsavel.map((r) => (
                    <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 40px', gap: 10, alignItems: 'center', fontSize: 13 }}>
                      <span>{r.nome.split(' ')[0]}</span><Progresso valor={r.qtd} total={prev.data!.porResponsavel[0]!.qtd} /><span className="num" style={{ textAlign: 'right' }}>{r.qtd}</span>
                    </div>
                  ))}
                </>}
                {prev.data.saldoContatosMes !== null && <span className="small muted num">Contatos disponíveis no mês: {num(prev.data.saldoContatosMes)}</span>}
                {prev.data.saldoContatosMes !== null && prev.data.elegiveis > prev.data.saldoContatosMes && (
                  <div className="alert warn">O público é maior que o saldo de contatos do mês. A fila vai parar quando o limite for atingido.</div>
                )}
              </section>
            )}
            {c.podeGerenciar && (
              <button className="btn primary xl" disabled={acao || !prev.data?.elegiveis}
                onClick={async () => { await executar('iniciar'); nav(`/campanhas/${c.id}`); }}>
                Iniciar campanha
              </button>
            )}
            <span className="small faint">Ao iniciar, a lista de clientes é congelada. Quem entrar no público depois não é incluído.</span>
          </aside>
        </div>
      ) : (
        <>
          <div className="grid-kpi">
            <div className="card kpi"><span className="label">Enviados</span><span className="value">{num(p.enviados)} <span className="small muted">/ {num(p.total)}</span></span><Progresso valor={p.enviados} total={p.total} /></div>
            <div className="card kpi"><span className="label">Pendentes</span><span className="value">{num(p.pendentes)}</span></div>
            <div className="card kpi"><span className="label">Pulados · bloqueados</span><span className="value">{num(p.pulados)} · {num(p.bloqueados)}</span></div>
            {p.email > 0 && (
              <div className="card kpi">
                <span className="label">E-mails (envio automático)</span>
                <span className="value">{num(p.emailEnviados)} <span className="small muted">/ {num(p.email)}</span></span>
                <span className="small muted">{num(p.emailPendentes)} na fila{p.falhas ? ` · ${num(p.falhas)} falharam` : ''}</span>
                {p.falhas > 0 && c.podeGerenciar && <button className="btn sm" disabled={acao} onClick={() => executar('email/reenviar-falhas')}>Reenviar falhas</button>}
              </div>
            )}
            {c.whatsapp && (
              <div className="card kpi">
                <span className="label">Clicaram no WhatsApp</span>
                <span className="value">{num(c.whatsapp.cliques)}</span>
                <span className="small muted">{c.whatsapp.comBotao ? `${Math.round((c.whatsapp.cliques / c.whatsapp.comBotao) * 100)}% dos ${num(c.whatsapp.comBotao)} e-mails com botão` : 'nenhum e-mail com botão ainda'}</span>
              </div>
            )}
            <div className="card kpi"><span className="label">Oportunidades</span><span className="value">{num(Number(c.oportunidades.criadas))}</span><span className="small muted">{num(Number(c.oportunidades.ganhas))} ganhas · {moeda(c.oportunidades.receita)}</span></div>
          </div>

          {c.whatsappBotao && <ClicaramWhatsapp campanhaId={c.id} statusId={c.whatsappStatusId ?? null} />}

          {c.porResponsavel.length > 1 && (
            <section className="card">
              <h2>Por vendedor</h2>
              {c.porResponsavel.map((r) => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 200px) 1fr 120px', gap: 12, alignItems: 'center', fontSize: 13.5 }}>
                  <span>{r.nome}</span><Progresso valor={r.enviados} total={r.total} /><span className="num small" style={{ textAlign: 'right' }}>{r.enviados}/{r.total}</span>
                </div>
              ))}
            </section>
          )}

          <section className="card flush">
            <div className="card-head" style={{ padding: '14px 16px' }}>
              <h2>Clientes da campanha</h2>
              <select className="select" style={{ width: 180 }} aria-label="Filtrar status" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                <option value="">Todos</option>
                {Object.entries(STATUS_ITEM).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {itens.isLoading ? <Carregando /> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Cliente</th><th>Canal</th><th>Contato</th><th>Responsável</th><th>Status</th><th>Enviado em</th></tr></thead>
                  <tbody>
                    {itens.data?.data.map((i) => (
                      <tr key={i.id}>
                        <td><Link to={`/clientes/${i.clienteId}`} style={{ color: 'var(--text)' }}>{i.nome}</Link>{i.empresa && <span className="small faint"> · {i.empresa}</span>}</td>
                        <td>{i.canal === 'EMAIL' ? <span className="badge blue">E-mail</span> : <span className="badge green">WhatsApp</span>}</td>
                        <td className="num">{i.canal === 'EMAIL' ? i.email : formatarWhatsapp(i.whatsapp)}</td>
                        <td>{i.responsavel}</td>
                        <td>
                          <span className={`badge ${STATUS_ITEM[i.status]?.cls ?? ''}`} title={i.motivo ?? undefined}>{i.canal === 'EMAIL' && i.status === 'ABERTO' ? 'Enviando' : STATUS_ITEM[i.status]?.label ?? i.status}</span>
                          {i.motivo && (i.status === 'FALHOU' || i.status === 'PENDENTE') && <span className="small faint" style={{ display: 'block', whiteSpace: 'normal', maxWidth: 260 }}>{i.motivo}</span>}
                        </td>
                        <td className="num">{dataHora(i.enviadoEm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}
